import React, {useCallback, useEffect, useRef, useState} from 'react';
import GridHeader from "../components/GridHeader";
import {
    createRepresentations,
    deleteRepresentations,
    getRepresentations
} from "../pages/reports/components/representation/api";
import Representation from "../pages/reports/components/representation/representation";
import {getColumns, getList} from "./api";
import {toast} from "react-toastify";
import ParcelsButtons from "./parcelsButtons";
import TableGrid from "../components/Table/TableGrid";
import {getFilterTypeByName, sortIsOn} from "./utils";
import {apiGet, apiPost} from "../services/apiClient";
import {getRoles} from '../services/userRights';
import debounce from "lodash.debounce";
import {Button, Dimmer, Segment} from "semantic-ui-react";
import Loader from "../components/Loader/Loader";
import DictionaryButtons from "./dictionaryButtons";
import {checkMatch, removeQueryStringFromUrl} from "../services/utils";
import qs from 'qs';

const GridPage = ({gridName, history, label, isDictionary = false, columns: cols, dictionaryData, preFilters, canEditByRow}) => {
    // Маркер версии для проверки загрузки правильного кода на стейдже
    useEffect(() => {
        console.log('🔍 GridPage v2.1 загружен - с поддержкой localStorage фильтров');
    }, []);
    
    const [loading, setLoading] = useState(false);
    const [disableFilters, setDisableFilters] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [height, setHeight] = useState(false);
    const [report, setReport] = useState(null);
    const [options, setOptions] = useState([]);
    const [columns, setColumns] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const rowCount = 50;
    const [filters, setFilters] = useState([]);
    const [filtersIsLoaded, setFiltersIsLoaded] = useState(false);
    const [order, setOrder] = useState(null);
    const [selectedIds, setSelected] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [rows, setRows] = useState([]);
    const [settingsForFilters, setSettingsForFilters] = useState({settings: {}});
    const [reportColumns, setReportColumns] = useState([]);
    const [currentSettings, setCurrentSettings] = useState(null);
    
    // Ref для предотвращения race conditions
    const loadingRequestRef = useRef(0);
    const isInitializedRef = useRef(false);
    const isChangingReportRef = useRef(false);
    
    // localStorage ключи для сохранения состояния
    const getStorageKey = (suffix, reportId = null) => {
        const baseKey = `grid_${gridName}`;
        if (reportId) {
            return `${baseKey}_report_${reportId}_${suffix}`;
        }
        return `${baseKey}_${suffix}`;
    };
    
    // Функция для загрузки состояния из localStorage для конкретного отчета
    const loadFromLocalStorage = (reportId = null) => {
        try {
            // Загружаем текущий reportId если не передан
            if (!reportId) {
                reportId = localStorage.getItem(getStorageKey('reportId'));
            }
            
            const savedFilters = localStorage.getItem(getStorageKey('filters', reportId));
            const savedOrder = localStorage.getItem(getStorageKey('order', reportId));
            const savedSearch = localStorage.getItem(getStorageKey('search', reportId));
            const isStored = savedFilters !== null || savedOrder !== null || savedSearch !== null;
            
            return {
                filters: savedFilters ? JSON.parse(savedFilters) : [],
                order: savedOrder ? JSON.parse(savedOrder) : null,
                searchQuery: savedSearch || '',
                reportId: reportId,
                isStored
            };
        } catch (e) {
            console.warn('Ошибка загрузки из localStorage:', e);
            return { filters: [], order: null, searchQuery: '', reportId: null, isStored: false };
        }
    };

    const hasGridState = (state) => {
        return !!(state && (
            (state.filters && state.filters.length)
            || state.order
            || state.searchQuery
        ));
    };

    const parseSettings = (settings) => {
        if (!settings) {
            return null;
        }
        if (typeof settings === 'object') {
            return settings;
        }
        try {
            return JSON.parse(settings);
        } catch (e) {
            console.warn('Ошибка разбора настроек представления:', e);
            return null;
        }
    };

    const loadFromReportSettings = (report) => {
        const result = {
            filters: [],
            order: null,
            searchQuery: '',
            reportId: report ? report.id : null
        };

        const reportSettings = parseSettings(report && (report.settings || report.Settings));
        if (reportSettings) {
            return {
                ...result,
                filters: reportSettings.filters || [],
                order: reportSettings.order || null,
                searchQuery: reportSettings.searchQuery || ''
            };
        }

        (report && report.columns ? report.columns : []).forEach(col => {
            const settings = parseSettings(col.settings || col.Settings);
            if (!settings) {
                return;
            }
            if (settings.filter) {
                result.filters.push(settings.filter);
            }
            if (settings.sort) {
                result.order = settings.sort;
            }
        });

        return result;
    };

    const loadReportState = (report) => {
        const savedState = loadFromLocalStorage(report && report.id);
        return savedState.isStored ? savedState : loadFromReportSettings(report);
    };
    
    // Функция для сохранения состояния в localStorage для текущего отчета
    const saveToLocalStorage = (data, reportId = null) => {
        try {
            // Используем текущий report.id если не передан reportId
            const currentReportId = reportId || (report && report.id);
            
            if (data.filters !== undefined) {
                // Если есть reportId, сохраняем для конкретного отчета, иначе - глобально для grid
                localStorage.setItem(getStorageKey('filters', currentReportId), JSON.stringify(data.filters));
            }
            if (data.order !== undefined) {
                localStorage.setItem(getStorageKey('order', currentReportId), JSON.stringify(data.order));
            }
            if (data.searchQuery !== undefined) {
                localStorage.setItem(getStorageKey('search', currentReportId), data.searchQuery);
            }
            if (data.reportId !== undefined) {
                localStorage.setItem(getStorageKey('reportId'), data.reportId);
            }
        } catch (e) {
            console.warn('Ошибка сохранения в localStorage:', e);
        }
    };

    const columnsFromReport = (report) => {
        return (report ? report.columns : [])
            .map(col => {
                const column = columns.find(c => c.id === col.name);
                if (!column || !column.id) {
                    return null;
                }
                return {
                    fieldName: column.id,
                    type: column.id,
                    title: column.displayNameKey,
                    tooltip: column.tooltip,
                    disableSort: !sortIsOn(column.id),
                    filterType: getFilterTypeByName(column.id),
                    canEdit: column.canEdit
                };
            })
            .filter(Boolean);
    }

    const settingsReport = (report, setSettings = true) => {
        setReport(report);
        saveToLocalStorage({ reportId: report ? report.id : null });
        
        if (report && columns) {
            if (setSettings) {
                // Проверяем, инициализирован ли компонент уже
                if (!isInitializedRef.current) {
                    const reportState = loadReportState(report);
                    if (hasGridState(reportState) && !hasGridState(getSettings())) {
                        setFilters(reportState.filters);
                        setOrder(reportState.order);
                        setSearchQuery(reportState.searchQuery);
                    }
                    // При первой загрузке фильтры уже восстановлены из localStorage/URL в useEffect
                    setReportColumns(columnsFromReport(report));
                    setFiltersIsLoaded(true);
                    setCurrentSettings(null);
                    isInitializedRef.current = true;
                } else {
                    // При смене отчета СРАЗУ сбрасываем старые данные и блокируем автозагрузку
                    setRows([]);
                    setSelected([]);
                    setLoading(true); // показываем индикатор загрузки
                    loadRowsDebounce.cancel(); // отменяем отложенные запросы
                    ++loadingRequestRef.current; // инвалидируем старые запросы
                    isChangingReportRef.current = true;
                    
                    // Загружаем сохраненные фильтры для этого отчета
                    const savedState = loadReportState(report);
                    
                    // Устанавливаем колонки и состояние одновременно
                    setReportColumns(columnsFromReport(report));
                    setFilters(savedState.filters);
                    setOrder(savedState.order);
                    setSearchQuery(savedState.searchQuery);
                    setFiltersIsLoaded(true);
                    
                    // После установки всех состояний разблокируем загрузку и принудительно загружаем данные
                    setTimeout(() => {
                        isChangingReportRef.current = false;
                        const newSettings = {
                            filters: savedState.filters || [],
                            order: savedState.order || null,
                            searchQuery: savedState.searchQuery || ''
                        };
                        
                        setCurrentSettings(newSettings);
                        loadRows(rowCount, 0, newSettings); // загружаем сразу без debounce
                    }, 0);
                }
            } else {
                setReportColumns(columnsFromReport(report));
            }
        }
    }

    useEffect(() => {
        if (reportColumns.length && (isDictionary || columns.length) && !isChangingReportRef.current) {
            const newSettings = getSettings();
            // Проверяем, изменились ли настройки
            const settingsChanged = !currentSettings || 
                JSON.stringify(currentSettings) !== JSON.stringify(newSettings);
            
            if (settingsChanged) {
                setCurrentSettings(newSettings);
                loadRowsDebounce(rowCount, 0, newSettings);
            }
        }
    }, [reportColumns, columns, order, filters, searchQuery])

    const getSettings = () => {
        return {
            filters: filters || [],
            order: order || null,
            searchQuery: searchQuery
        };
    }

    useEffect(() => {
        setSettingsForFilters({settings: getSettings()})
    }, [filters])

    const loadRows = (takeCount, skipCount, settings, onComplete) => {
        // Увеличиваем счетчик запросов для предотвращения race conditions
        const currentRequestId = ++loadingRequestRef.current;
        setLoading(true);
        
        getList(gridName, {
            settings,
            takeCount,
            skipCount,
            searchQuery: settings.searchQuery
        }, !isDictionary).then(data => {
            // Проверяем, что это последний запрос
            if (currentRequestId === loadingRequestRef.current) {
                let rs;
                if (skipCount === 0) {
                    rs = data.entries || [];
                    scrollToTop();
                }
                else rs = [...rows, ...(data.entries || [])]
                setRows(rs || []);
                data.totalCount && setTotalCount(data.totalCount);
                setLoading(false);
                // Вызываем callback после завершения загрузки
                if (onComplete) {
                    onComplete();
                }
            }
        }).catch(() => {
            if (currentRequestId === loadingRequestRef.current) {
                setLoading(false);
                toast.error('Ошибка загрузки');
            }
        })
    }

    const loadRowsDebounce = useCallback(debounce(loadRows, 300), []);

    const loadNextPart = () => {
        if ((rows.length < totalCount) && !loading) {
            const settings = getSettings();
            loadRows(rowCount, rows.length, settings);
        }
    };

    const loadData = () => {
        if (isDictionary) {
            apiGet(`grid/settings/${gridName}`).then((d) => {
                if (d) {
                    setFilters(d.filters || []);
                    setOrder(d.order);
                    setFiltersIsLoaded(true);
                }
            })
        } else {
            getRepresentations(gridName).then(d => {
                setOptions(d.grids.map(r => ({
                    value: r.id,
                    key: r.id,
                    text: r.name,
                    data: r
                })))
            });
            getColumns(gridName).then(d => {
                setColumns(d.columns.map(c => ({
                    id: c.name,
                    displayNameKey: c.description,
                    tooltip: c.tooltip,
                    canEdit: c.isEditable
                })))
            })
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        if (cols && cols.length) {
            setReportColumns(cols.filter(col => !col.rolesForView || checkMatch(getRoles(), col.rolesForView)))
        }
    }, [cols]);

    const saveFilters = (report, filters, order) => {
        if (!isDictionary) {
            const form = {
                ...report,
                columns: report.columns.map(col => {
                    let column = {...col};
                    const filter = filters.find(f => f.fieldName === col.name) || null;
                    const sort = (order && (order.fieldName === col.name)) ? order : null;
                    column.settings = (filter || sort) ? JSON.stringify({filter, sort}) : null
                    return column;
                })
            }
            save(form, false);
        } else {
            apiPost(`grid/settings/${gridName}`, {
                filters,
                order
            }).then(() => {})
        }
    }

    const saveFilterDebounce = useCallback(debounce(saveFilters, 2000), []);

    useEffect(() => {
        if (!isDictionary && options && columns.length) {
            // Пытаемся восстановить сохраненный отчет из localStorage
            const savedState = loadFromLocalStorage();
            let selectedReport = null;
            
            if (savedState.reportId) {
                selectedReport = options.find(o => o.value == savedState.reportId);
            }
            
            // Если сохраненный отчет не найден, используем дефолтный
            if (!selectedReport) {
                const index = options.findIndex(o => report ? (o.value == report.id) : (o.data || {}).isDefault);
                const defaultOptionIndex = index !== -1 ? index : 0;
                selectedReport = options[defaultOptionIndex];
            }
            
            if (selectedReport && (!report || selectedReport.value != report.id)) {
                settingsReport(selectedReport.data, true);
            }
        }
    }, [options, columns])

    const roH = useRef(new ResizeObserver(el => {
        setHeight(el[0].target.clientHeight);
    }));

    const resizeH = useCallback((container) => {
        if (container) roH.current.observe(container);
        else if (roH.current) {
            roH.current.disconnect();
            setHeight(0);
        }
    }, [roH.current]);

    const downloadExcel = () => {
        setLoading(true)
        const settings = getSettings();
        apiPost(`${gridName}${isDictionary ? '' : '/grid'}/excel`, {
            settings: settings,
            columns: !isDictionary && (reportColumns || []).map(c => c.fieldName),
            gridName: !isDictionary && report.name,
            searchQuery: settings.searchQuery
        })
            .then(data => {
                setLoading(false)
                window.location.href = getApiBaseUrl() + 'files/' + data.result;
            })
            .catch(err => {
                setLoading(false);
                console.log(err)
                toast.error('Произошла ошибка')
            });
    }

    const save = (form, load = true) => {
        setSaveLoading(true)
        return createRepresentations(gridName, form).then((result) => {
            const savedId = (result && result.id) || form.id;
            if (!load) {
                return;
            }
            return getRepresentations(gridName).then(d => {
                const newOptions = d.grids.map(r => ({
                    value: r.id,
                    key: r.id,
                    text: r.name,
                    data: r
                }));
                setOptions(newOptions);
                const savedOption = newOptions.find(o => o.value == savedId);
                if (savedOption) {
                    settingsReport(savedOption.data, true);
                }
            });
        }).then(() => {
            setSaveLoading(false)
        }).catch(() => {
            setSaveLoading(false)
            toast.error('Произошла ошибка при сохранении отчета')
            return Promise.reject();
        })
    }

    const onDelete = (id) => {
        setSaveLoading(true)
        deleteRepresentations(gridName, id).then(() => {
            setSaveLoading(false)
            loadData();
            toast.success('Отчет успешно удален')
        }).catch(() => {
            setSaveLoading(false)
            toast.error('Произошла ошибка при удалении отчета')
        })
    }

    const updateSort = (fieldName) => {
        const oldOrder = (order || {}).fieldName === fieldName ? order.order : null;
        const newOrder = oldOrder === 'desc' ? null : {
            fieldName,
            order: !oldOrder ? 'asc' : 'desc'
        };
        setOrder(newOrder);
        saveToLocalStorage({ order: newOrder });
        saveFilterDebounce(report, filters, newOrder);
    }

    const newFiltersArrayByFilter = (f, filters) => {
        const filterIndex = filters.findIndex(fl => fl.fieldName === f.fieldName);
        const prevFilters = [...filters];
        filterIndex !== -1 ? prevFilters[filterIndex] = f : prevFilters.push(f)
        // Удаляем фильтры с пустыми значениями или null/undefined
        return prevFilters.filter(f => f && f.value && f.value.toString().trim() !== '');
    }
    // --- ВОССТАНОВЛЕНИЕ ФИЛЬТРОВ И ПОИСКА ИЗ URL (только если нет localStorage данных) ---
    useEffect(() => {
        // Только при первом монтировании
        if (!gridName) return; // Ждем пока gridName будет установлен
        
        const savedState = loadFromLocalStorage();
        
        // Если есть данные в localStorage, приоритет у них
        if (savedState.filters.length || savedState.searchQuery || savedState.order) {
            setFilters(savedState.filters);
            setSearchQuery(savedState.searchQuery);
            setOrder(savedState.order);
            setFiltersIsLoaded(true);
            return;
        }
        
        // Если localStorage пуст, пытаемся восстановить из URL
        const params = qs.parse(window.location.search, { ignoreQueryPrefix: true });
        let restoredFilters = [];
        let restoredSearch = '';
        if (params.filters) {
            try {
                restoredFilters = JSON.parse(params.filters);
            } catch (e) {}
        }
        if (params.searchQuery) {
            restoredSearch = params.searchQuery;
        }
        if (restoredFilters.length) setFilters(restoredFilters);
        if (restoredSearch) setSearchQuery(restoredSearch);
        setFiltersIsLoaded(true);
    }, [gridName]);

    // Внутренние фильтры/поиск не синхронизируем обратно в URL.
    // URL выступает только как внешний источник начальных условий.

    // --- ОБНОВЛЕНИЕ ПОИСКА ---
    const handleSearchChange = useCallback((value) => {
        // Сразу обновляем состояние для отображения в поле ввода
        setSearchQuery(value);
        saveToLocalStorage({ searchQuery: value });
        // Сброс currentSettings чтобы useEffect сработал 
        setCurrentSettings(null);
        // Загрузка данных будет выполнена через useEffect с debounce
    }, [report]);

    const clearSearch = () => {
        setSearchQuery('');
        saveToLocalStorage({ searchQuery: '' });
        // Сброс currentSettings чтобы useEffect сработал 
        setCurrentSettings(null);
        // Загрузка данных будет выполнена через useEffect с debounce
    };

    // --- ОБНОВЛЕНИЕ ФИЛЬТРОВ ---
    const updateFilter = (f, notSave = false, replace = false) => {
        let newFilters = replace ? [] : [...filters];
        if (typeof f === 'string') {
            const filterObj = {
                fieldName: f,
                value: notSave,
                operation: 'contains'
            };
            newFilters = newFiltersArrayByFilter(filterObj, newFilters);
        } else if (Array.isArray(f)) {
            f.forEach(fv => {
                newFilters = newFiltersArrayByFilter(fv, newFilters);
            });
        } else {
            newFilters = newFiltersArrayByFilter(f, newFilters);
        }
        setFilters(newFilters);
        setSelected([]);
        saveToLocalStorage({ filters: newFilters });
        // Сброс currentSettings чтобы useEffect сработал 
        setCurrentSettings(null);
        // Загрузка данных будет выполнена через useEffect с debounce
        !notSave && saveFilterDebounce(report, newFilters, order);
    }

    const setSelectedAllRequest = (isSelect) => {
        if (isSelect && !(selectedIds && selectedIds.length > 0)) {
            const settings = getSettings();
            setDisableFilters(true);
            apiPost(`${gridName}${isDictionary ? '' : '/grid'}/ids`, {
                settings: settings,
                searchQuery: settings.searchQuery
            }).then(d => {
                setSelected(d.ids);
                setDisableFilters(false);
            }).catch(e => {
                console.log(e);
                setDisableFilters(false);
            });
        } else {
            setSelected([]);
        }
    }

    const ref = useRef();

    const scrollToTop = () => {
        if (ref.current) ref.current.scrollTop = 0;
    }

    useEffect(() => {
        if (filters) {
            scrollToTop();
        }
    }, [filters, order])

    const selectAll = (isSelect) => {
        setSelectedAllRequest(isSelect);
    }

    const clearSelected = () => {
        setSelected([]);
    }

    const roles = getRoles() || [];
    const isCoordinator = roles.includes('Coordinator');
    const isAuthor = roles.includes('Author') || roles.includes('Demo') || isCoordinator;

    const clearFiltersAndSort = () => {
        preFilters && removeQueryStringFromUrl();
        setFilters([]);
        setOrder(null);
        setSearchQuery('');
        setCurrentSettings(null);
        // Очищаем фильтры для текущего отчета/grid
        saveToLocalStorage({ filters: [], order: null, searchQuery: '' });
        saveFilterDebounce(report, [], null);
    }
    const setPreFilters = () => {
        //устанавливает фильтры из location.search ВМЕСТО сохраненных фильтров
        //фильтры из location.search НЕ сохраняются у пользователя, если он не поменяет какой-либо фильтр вручную
        
        // Проверяем, есть ли уже сохраненные пользователем фильтры
        const savedState = loadFromLocalStorage();
        if (savedState.filters.length || savedState.searchQuery || savedState.order) {
            return; // Не применяем preFilters если есть пользовательские
        }
        setCurrentSettings(null);
        updateFilter(Object.keys(preFilters).map(f => ({
            fieldName: f,
            value: preFilters[f],
            operation: 'contains'
        })), true, true)
    }

    useEffect(() => {
        (preFilters && filtersIsLoaded) && setPreFilters();
    }, [preFilters, filtersIsLoaded]);

    const reload = (onComplete) => {
        const settings = getSettings();
        setCurrentSettings(settings);
        loadRows(rowCount, 0, settings, onComplete);
    }

    return (
        <div className='new-styles new-styles__p-0'>
            <div className='grid__header' ref={resizeH}>
                <GridHeader
                    input={
                        !isDictionary && <Representation
                            loading={saveLoading}
                            columns={columns}
                            options={options}
                            value={report}
                            setValue={(v) => {
                                settingsReport(v)
                            }}
                            save={save}
                            onDelete={onDelete}
                        />
                    }
                    loading={loading || disableFilters}
                    name={label}
                    history={history}
                    canCreate={isAuthor && !isDictionary}
                    downloadExcel={downloadExcel}
                    searchQuery={searchQuery}
                    onSearchChange={handleSearchChange}
                    onSearchClear={clearSearch}
                    btn={(dictionaryData || {}).createBtnText && <Button
                        className='btn_light-blue btn_icon_plus'
                        onClick={() => history.push(`/dictionary/${gridName}/new`)}
                    >
                        {dictionaryData.createBtnText}
                    </Button>}
                />
                {!isDictionary && <ParcelsButtons
                    selectedItems={selectedIds}
                    reload={reload}
                    setLoading={setLoading}
                    loading={loading}
                    history={history}
                    clearDisabled={!((filters || []).length || order)}
                    clearFilters={clearFiltersAndSort}
                    clearSelected={clearSelected}
                />}
                {isDictionary && <DictionaryButtons
                    dictionaryData={dictionaryData}
                    selectedItems={selectedIds}
                    reload={reload}
                    setLoading={setLoading}
                    loading={loading}
                    history={history}
                    clearDisabled={!((filters || []).length || order)}
                    clearFilters={clearFiltersAndSort}
                />}
            </div>
            <Segment vertical>
                <Dimmer className='grid__dimmer' active={loading} inverted>
                    <Loader label={'Загрузка'}/>
                </Dimmer>
                <div
                    className='grid__tbl scroll-style'
                    style={{
                        height: `calc(100vh - ${height}px)`
                    }}
                    ref={ref}
                >
                    <TableGrid
                        key={report
                            ? `${report.id}-${reportColumns.map(c => c.fieldName).join(',')}`
                            : 'no-report'}
                        disableFilters={disableFilters}
                        gridName={gridName}
                        isDictionary={isDictionary}
                        isGrid
                        selectedIds={selectedIds}
                        setAll={selectAll}
                        setSelected={(id, value) => {
                            setSelected(ids => {
                                value ? ids = [...(ids || []), id] : ids = ids.filter(i => i !== id);
                                return ids;
                            })
                        }}
                        totalCount={totalCount}
                        rowCount={rowCount}
                        columns={reportColumns}
                        checkbox
                        updateFilter={updateFilter}
                        filters={filters}
                        rows={rows}
                        setSort={updateSort}
                        sort={order}
                        loading={loading}
                        infinityScroll
                        striped={false}
                        options={settingsForFilters}
                        loadNextPart={loadNextPart}
                        history={history}
                        reload={reload}
                        canEditByRow={canEditByRow}
                    />
                </div>
            </Segment>
        </div>
    );
};

export default GridPage;


// WEBPACK FOOTER //
// ./App/grids/gridPage.jsx